import type { I18nLocale } from "@/modules/i18n/types"
import type { FunctionComponent } from "react"

import { clsx } from "clsx"

import { FLAG_ASSET } from "@/modules/headerFooterFO/flags/assets"
import { Icon } from "@/modules/headerFooterFO/Header/components/Icon/Icon"
import { useTranslation } from "@/modules/i18n/components/useTranslation"
import { useFoLayoutContext } from "@/modules/layouts/contexts/foLayout"
import { useLocale } from "@/modules/locales/useLocale"
import { Link } from "@/modules/routing/components/Link"

import styles from "./LocalesDropdown.module.css"

export type LocalesDropdownProps = {
  from: "footer" | "header"
}

export const LocalesDropdown: FunctionComponent<LocalesDropdownProps> = ({ from }) => {
  const { t } = useTranslation(["module_header_fo", "shared_languages"])
  const currentLocale = useLocale()
  const { allowedLocales } = useFoLayoutContext()

  return (
    <div className={styles["hdr-DropdownMenu"]}>
      <div
        className={clsx(styles["hdr-DropdownMenu-item"], styles["hdr-DropdownMenu-item--flag"], {
          [styles["hdr-DropdownMenu-item--flagOnTop"]]: from === "header",
          [styles["hdr-DropdownMenu-item--flagOnBottom"]]: from === "footer",
        })}
        // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
        tabIndex={0} // useful for Safari
      >
        <button
          type="button"
          className={clsx(styles["hdr-DropdownMenu-link"], {
            [styles["hdr-DropdownMenu-link--flagOnTop"]]: from === "header",
            [styles["hdr-DropdownMenu-link--flagOnBottom"]]: from === "footer",
          })}
          data-icon="expandMore|right"
          aria-label={t("module_header_fo.language") as string}
        >
          <span className={styles["hdr-DropdownMenu-linkIcon"]}>
            <img
              src={FLAG_ASSET[currentLocale]}
              width="18"
              height="18"
              style={{ display: "block" }}
              alt="Current locale flag"
            />
          </span>
          <span className={styles["hdr-DropdownMenu-linkLabel"]}>{t(`shared_languages.${currentLocale}`)}</span>
          <Icon name="ArrowDown" className={styles["hdr-DropdownMenu-arrowIcon"]} />
        </button>
        <ul className={`js-DropdownMenu-submenu ${styles["hdr-DropdownMenu-submenu"]}`}>
          {Object.entries(allowedLocales).map(([locale, href]) => (
            <li
              data-dropdown-locale={locale}
              className={clsx(styles["hdr-DropdownMenu-submenuItem"], {
                [styles["hdr-DropdownMenu-submenuItem--selected"]]: currentLocale === locale,
              })}
              key={locale}
            >
              <Link href={href} className={styles["hdr-DropdownMenu-submenuLink"]}>
                <figure className={styles["hdr-DropdownMenu-submenuLinkItem"]}>
                  <img
                    src={FLAG_ASSET[locale as I18nLocale]}
                    width="18"
                    height="18"
                    style={{ display: "block" }}
                    alt={`Flag for ${t(`shared_languages.${locale}`)}`}
                  />
                  <figcaption className={styles["hdr-DropdownMenu-submenuLinkText"]}>
                    {t(`shared_languages.${locale}`)}
                  </figcaption>
                </figure>
              </Link>
            </li>
          ))}
        </ul>
      </div>
    </div>
  )
}
